import type { MarkerProps } from '@react-google-maps/api';
import { Autocomplete } from '@react-google-maps/api';
import * as React from 'react';
import { Row, Col } from 'react-bootstrap';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Field, change } from 'redux-form';

import * as AddressUtils from 'af-utils/address.util';

import CanadaProvinces from 'ab-enums/canadaProvinces.enum';
import { USStates } from 'ab-enums/usStates.enum';

import { DEFAULT_MAP_ZOOM_LEVEL_ON_CENTER } from 'af-constants/values';

import DropdownField from 'af-fields/Dropdown';
import Input from 'af-fields/Input';
import Textarea from 'af-fields/Textarea';

import * as AddressesActions from 'af-actions/addresses';

import MapRow from './MapRow';
import styles from './styles.module.scss';
import NonEditableMap from '../NonEditableMap';
import GoogleScriptLoader from '../Shared/Loader';

interface OwnProps {
	autosave?: () => Promise<void>;
	addressType: AddressType;
	onValueUpdate?: () => void;
	onAddressTypeChange: (addressType: AddressType) => void;
	formName: string;
	locationPropName: string;
	streetNumberPropName: string;
	routePropName: string;
	cityPropName: string;
	neighborhoodPropName: string;
	countryPropName: string;
	backupPropName: string;
	disabled?: boolean;
	statePropName: string;
	notesPropName: string;
	postalCodePropName: string;
	latitude: Nullable<number>;
	longitude: Nullable<number>;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

export enum AddressType {
	US = 'US',
	CAN = 'CAN',
	OTHER = 'OTHER'
}

interface FieldColumnWidthValues {
	addressType: number;
	country: number;
	location: number;
	state: number;
	zip: number;
}

const defaults = {
	center: { lat: 38.7099085, lng: -95.137812 },
	zoom: 3,
};

export const AddressTypeLabel: { [key in keyof typeof AddressType]: string; } = {
	[AddressType.US]: 'United States',
	[AddressType.CAN]: 'Canada',
	[AddressType.OTHER]: 'Other',
};

interface AddressTypeOption {
	id: AddressType;
	name: string;
}

const StateLabel = {
	[AddressType.US]: 'State',
	[AddressType.CAN]: 'Province/Territory',
	[AddressType.OTHER]: 'State/Province/Country/Territory',
};

const StateComponent = {
	[AddressType.US]: DropdownField,
	[AddressType.CAN]: DropdownField,
	[AddressType.OTHER]: Input,
};

const StatePlaceholder = {
	...StateLabel,
	[AddressType.OTHER]: 'State',
};

const ZipLabel = {
	[AddressType.US]: 'Zip',
	[AddressType.CAN]: 'Postal Code',
	[AddressType.OTHER]: 'Zip/Postal Code',
};

const addressTypeOptionsMap = {
	[AddressType.US]: { id: AddressType.US, name: AddressTypeLabel[AddressType.US] },
	[AddressType.CAN]: { id: AddressType.CAN, name: AddressTypeLabel[AddressType.CAN] },
	[AddressType.OTHER]: { id: AddressType.OTHER, name: AddressTypeLabel[AddressType.OTHER] },
};

const addressTypeOptions = Object.values(addressTypeOptionsMap);

interface StateOption {
	name: string;
}

const usStateOptions: StateOption[] = Object.entries(USStates)
	.map(([, name]) => ({ name }));

const canadaStateOptions: StateOption[] = Object.entries(CanadaProvinces)
	.map(([, name]) => ({ name }));

const STATE_OPTIONS_BY_ADDRESS_TYPE = {
	[AddressType.US]: usStateOptions,
	[AddressType.CAN]: canadaStateOptions,
	[AddressType.OTHER]: [...usStateOptions, ...canadaStateOptions],
};

const DEFAULT_AUTOCOMPLETE_OPTIONS = {
	componentRestrictions: { country: ['us', 'ca'] },
	strictBounds: true,
};

const fieldColumnWidthValues: { [key in keyof typeof AddressType]: FieldColumnWidthValues; } = {
	[AddressType.US]: {
		addressType: 6,
		country: 0,
		location: 8,
		state: 6,
		zip: 4,
	},
	[AddressType.CAN]: {
		addressType: 6,
		country: 0,
		location: 8,
		state: 6,
		zip: 4,
	},
	[AddressType.OTHER]: {
		addressType: 4,
		country: 4,
		location: 8,
		state: 4,
		zip: 4,
	},
};

const addressTypeStates: { [key in keyof typeof AddressType]: string[]; } = {
	[AddressType.US]: ['us'],
	[AddressType.CAN]: ['ca'],
	[AddressType.OTHER]: ['us', 'ca'],
};

const _getAutocompleteOptions = (_addressType: AddressType) => {
	return {
		...DEFAULT_AUTOCOMPLETE_OPTIONS,
		componentRestrictions: { country: addressTypeStates[_addressType] },
	};
};

const AddressMap: React.FC<Props> = (props) => {
	const {
		locationPropName,
		cityPropName,
		neighborhoodPropName,
		backupPropName,
		postalCodePropName,
		countryPropName,
		routePropName,
		disabled,
		onValueUpdate,
		autosave,
		changeField,
		onAddressTypeChange,
		getAddressByLatLng,
		formName,
		addressType,
		notesPropName,
		statePropName,
		streetNumberPropName,
		latitude,
		longitude,
	} = props;

	const [mapVisible, setMapVisible] = React.useState(false);
	const [marker, setMarker] = React.useState<Nullable<MarkerProps>>(null);
	const [center, setCenter] = React.useState<google.maps.LatLng | google.maps.LatLngLiteral>(defaults.center);
	const [zoom, setZoom] = React.useState(defaults.zoom);
	const [searchResult, setSearchResult] = React.useState<Nullable<google.maps.places.Autocomplete>>(null);
	const [autocompleteOptions, setAutocompleteOptions] = React.useState(_getAutocompleteOptions(addressType));

	// On mount
	React.useEffect(() => {
		if (latitude && longitude) {
			const latLng = {
				lat: latitude,
				lng: longitude,
			};
			const _marker: MarkerProps = {
				position: latLng,
				animation: 1,
			};

			setMarker(_marker);
			setCenter(latLng);
			setZoom(DEFAULT_MAP_ZOOM_LEVEL_ON_CENTER);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [latitude, longitude]);

	const updateCountry = React.useCallback(() => {
		const newCountryValue = addressType === AddressType.OTHER ? null : AddressTypeLabel[addressType];
		if (newCountryValue) {
			changeField(formName, countryPropName, newCountryValue);
		}
	}, [addressType, changeField, countryPropName, formName]);

	React.useEffect(() => {
		setAutocompleteOptions(_getAutocompleteOptions(addressType));
		updateCountry();
	}, [addressType, changeField, countryPropName, formName, updateCountry]);

	const changeMapVisibility = React.useCallback(() => setMapVisible(!mapVisible), [mapVisible]);

	const onAddressTypeValueChange = React.useCallback((option: AddressTypeOption) => {
		const newCountryValue = option.id === AddressType.OTHER ? null : option.name;

		changeField(formName, locationPropName, null);
		changeField(formName, statePropName, null);
		changeField(formName, postalCodePropName, null);
		changeField(formName, countryPropName, newCountryValue);
		changeField(formName, streetNumberPropName, null);
		changeField(formName, cityPropName, null);
		changeField(formName, neighborhoodPropName, null);
		changeField(formName, routePropName, null);
		changeField(formName, backupPropName, null);
		changeField(formName, notesPropName, null);

		onAddressTypeChange(option.id);
	}, [
		backupPropName,
		changeField,
		cityPropName,
		countryPropName,
		formName,
		locationPropName,
		neighborhoodPropName,
		notesPropName,
		onAddressTypeChange,
		postalCodePropName,
		routePropName,
		statePropName,
		streetNumberPropName,
	]);

	const updateFields = React.useCallback((address: Nullable<AddressUtils.FormattedAddress>, location: google.maps.LatLng) => {
		if (!address) {
			return;
		}

		const streetNumber = address.streetNumber;
		const city = address.city;
		const neighborhood = address.neighborhood;
		const street = address.street;

		const _location: string[] = [];
		if (street) {
			_location.push(street);
		}
		if (neighborhood) {
			_location.push(neighborhood);
		}
		if (city) {
			_location.push(city);
		}
		changeField(formName, locationPropName, `${streetNumber ? streetNumber : ''} ${_location.join(', ')}`);
		changeField(formName, statePropName, address.state ?? '');
		changeField(formName, postalCodePropName, address.postalCode ?? '');
		changeField(formName, countryPropName, address.country ?? '');
		changeField(formName, cityPropName, address.city ?? '');
		changeField(formName, neighborhoodPropName, address.neighborhood ?? '');
		changeField(formName, routePropName, address.street ?? '');
		changeField(formName, streetNumberPropName, address.streetNumber ?? '');
		changeField(formName, backupPropName, {
			latitude: location.lat(),
			longitude: location.lng(),
			aa1: address.aa1,
			aa2: address.aa1,
			aa3: address.aa1,
			neighborhood: address.neighborhood,
			country: address.country,
			locality: address.state,
			postalCode: address.postalCode,
			street: address.street,
			streetNumber: address.streetNumber,
			suite: null,
			postalOfficeBoxCode: null,
			formattedAddress: address.formattedAddress,
		});

		if (onValueUpdate) {
			onValueUpdate();
		}
		if (autosave) {
			autosave();
		}
	}, [
		autosave,
		backupPropName,
		changeField,
		countryPropName,
		formName,
		cityPropName,
		locationPropName,
		onValueUpdate,
		postalCodePropName,
		statePropName,
		neighborhoodPropName,
		routePropName,
		streetNumberPropName,
	]);

	const onMapClick = React.useCallback(async ({ latLng }: google.maps.MapMouseEvent) => {
		if (!latLng) {
			throw new Error('Coordinates not found');
		}

		const _marker: MarkerProps = {
			position: latLng,
			animation: 1,
		};

		setMarker(_marker);
		setCenter(latLng);
		setZoom(DEFAULT_MAP_ZOOM_LEVEL_ON_CENTER);

		const address = latLng ? await getAddressByLatLng(latLng.lat(), latLng.lng()) : null;
		updateFields(address, latLng);
	}, [getAddressByLatLng, updateFields]);

	const onPlacesChanged = React.useCallback(() => {
		const place = searchResult?.getPlace();
		if (!place?.geometry?.location) {
			return;
		}

		const _marker: MarkerProps = {
			position: place.geometry.location,
			animation: 1,
		};

		setMarker(_marker);
		setCenter(place.geometry.location);
		setZoom(DEFAULT_MAP_ZOOM_LEVEL_ON_CENTER);

		const address = AddressUtils.formatAddress(place);
		updateFields(address, place.geometry.location);
	}, [searchResult, updateFields]);

	const onLocationChange = React.useCallback((value: string) => {
		if (!value) {
			// Country intentionally not changed to null, we don't want to reset that. Others we do.
			changeField(formName, locationPropName, null);
			changeField(formName, statePropName, null);
			changeField(formName, postalCodePropName, null);
			changeField(formName, streetNumberPropName, null);
			changeField(formName, cityPropName, null);
			changeField(formName, neighborhoodPropName, null);
			changeField(formName, routePropName, null);
			changeField(formName, backupPropName, null);
		}

		changeField(formName, routePropName, value);
		updateCountry();
		if (onValueUpdate) {
			onValueUpdate();
		}
	}, [
		backupPropName,
		updateCountry,
		changeField,
		cityPropName,
		formName,
		locationPropName,
		neighborhoodPropName,
		onValueUpdate,
		postalCodePropName,
		routePropName,
		statePropName,
		streetNumberPropName,
	]);

	const handleStreetFieldMount = React.useCallback((autocomplete: google.maps.places.Autocomplete) => {
		if (!autocomplete) {
			return;
		}
		setSearchResult(autocomplete);
	}, []);

	const renderMap = () => {
		if (disabled) {
			return (
				<NonEditableMap
					location={center}
				/>
			);
		}
		return (
			<MapRow
				center={center}
				marker={marker}
				onMapClick={onMapClick}
				zoom={zoom}
			/>
		);
	};

	const columnWidthValues = fieldColumnWidthValues[addressType];

	return (
		<div>
			<Row>
				<Col className={styles['address__address-column--no-padding']} sm={columnWidthValues.addressType}>
					<Field
						component={DropdownField}
						defaultValue={addressTypeOptionsMap[addressType]}
						disabled={disabled}
						label="Country"
						labelKey="name"
						name="country"
						onValueChange={onAddressTypeValueChange}
						options={addressTypeOptions}
						withCaret={true}
					/>
				</Col>
				{!!columnWidthValues.country &&
					<Col sm={columnWidthValues.country}>
						<Field
							className={styles['address__autocomplete-input']}
							component={Input}
							disabled={disabled}
							id={countryPropName}
							label="Country"
							name={countryPropName}
							onValueChange={onValueUpdate}
							placeholder="Country"
							type="text"
						/>
					</Col>
				}
				<Col sm={columnWidthValues.location}>
					<GoogleScriptLoader>
						<Autocomplete
							onLoad={handleStreetFieldMount}
							onPlaceChanged={onPlacesChanged}
							options={autocompleteOptions}
						>
							<Field
								additionalAction={changeMapVisibility}
								additionalActionIcon="LocationDefault"
								component={Input}
								disabled={disabled}
								id={locationPropName}
								label="Address, City or Location"
								name={locationPropName}
								onValueChange={onLocationChange}
								placeholder="Address, City or Location"
								type="text"
							/>
						</Autocomplete>
					</GoogleScriptLoader>
				</Col>
				<Col sm={columnWidthValues.state}>
					<Field
						component={StateComponent[addressType]}
						disabled={disabled}
						id={statePropName}
						label={StateLabel[addressType]}
						labelKey="name"
						name={statePropName}
						onValueChange={onValueUpdate}
						options={STATE_OPTIONS_BY_ADDRESS_TYPE[addressType]}
						placeholder={StatePlaceholder[addressType]}
						type="text"
						valueKey="name"
						withCaret={true}
					/>
				</Col>
				<Col sm={columnWidthValues.zip}>
					<Field
						component={Input}
						disabled={disabled}
						id={postalCodePropName}
						label={ZipLabel[addressType]}
						name={postalCodePropName}
						onValueChange={onValueUpdate}
						placeholder={ZipLabel[addressType]}
						type="text"
					/>
				</Col>
			</Row>
			<Row>
				<Col className={styles['address__address-column--no-padding']} sm={24}>
					<Field
						component={Textarea}
						disabled={disabled}
						id={notesPropName}
						maxCharacters={150}
						name={notesPropName}
						onValueChange={onValueUpdate}
						placeholder="Work location notes, pickup location, markers, etc."
						rows={3}
						showMaxCharactersLabel={true}
					/>
				</Col>
			</Row>
			{mapVisible && renderMap()}
		</div>
	);
};

const connector = connect(null, mapDispatchToProps());

function mapDispatchToProps() {
	return {
		changeField: change,
		getAddressByLatLng: AddressesActions.getAddressByLatLngNew,
	};
}

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(AddressMap);
